import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import { useEncryptService } from "../../shared/hooks";

type MfaStatus = {
  mfa_enabled: boolean;
  mfa_method: string | null;
};

const MfaSettings = ({ ssoEnabled }: { ssoEnabled?: boolean }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { doEncryptLogin } = useEncryptService();

  const [status, setStatus] = useState<MfaStatus>({
    mfa_enabled: false,
    mfa_method: null,
  });
  const [loading, setLoading] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [pendingEnable, setPendingEnable] = useState(false);
  const [password, setPassword] = useState("");

  const loadStatus = async () => {
    try {
      const response = await axiosInstance.post(END_POINTS.MFA, { action: "status" });
      if (response.status === 200) {
        setStatus({
          mfa_enabled: !!response.data?.data?.mfa_enabled,
          mfa_method: response.data?.data?.mfa_method || null,
        });
      }
    } catch (error: any) {
      // Non-blocking on profile load
      console.error("Failed to load MFA status", error);
    }
  };

  useEffect(() => {
    if (!ssoEnabled) {
      loadStatus();
    }
  }, [ssoEnabled]);

  if (ssoEnabled) {
    return null;
  }

  const openConfirm = (enable: boolean) => {
    setPendingEnable(enable);
    setPassword("");
    setPasswordModalOpen(true);
  };

  const submitToggle = async () => {
    if (!password) {
      toast.error("Enter your password to continue");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post(END_POINTS.MFA, {
        action: pendingEnable ? "enable" : "disable",
        method: "email",
        password: doEncryptLogin(password),
      });
      setStatus({
        mfa_enabled: !!response.data?.data?.mfa_enabled,
        mfa_method: response.data?.data?.mfa_method || null,
      });
      toast.success(response.data?.message || "MFA settings updated");
      setPasswordModalOpen(false);
      setPassword("");
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || error.message || "Unable to update MFA"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: isDarkMode ? "#2D2D2D" : "#F7F7F9",
          p: 2,
          borderRadius: 3,
          mb: 2,
          color: isDarkMode ? "#ffffff" : "#000000",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Email multi-factor authentication
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkMode ? "#a0a0a0" : "#666" }}>
              Require a one-time code sent to your email when signing in
            </Typography>
          </Box>
          <Switch
            checked={!!status.mfa_enabled}
            onChange={(_, checked) => openConfirm(checked)}
            disabled={loading}
            color="primary"
          />
        </Box>
      </Box>

      <Modal open={passwordModalOpen} onClose={() => !loading && setPasswordModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: isDarkMode ? "#1e1e1e" : "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            color: isDarkMode ? "#ffffff" : "#000000",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            {pendingEnable ? "Enable Email MFA" : "Disable MFA"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your password to confirm this change.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              disabled={loading}
              onClick={() => setPasswordModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading || !password}
              onClick={submitToggle}
              endIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default MfaSettings;
